/**
 * Client application root — the interactive, hydrated counterpart to the
 * server-rendered `AppShell`.
 *
 * It renders the SAME landmark layout + navigation the server produced (so
 * hydration matches the server markup) but injects the browser service
 * callbacks (`onRegister`, `onLogin`, `onVerify`, ...) into the screens that
 * need them. Those callbacks talk to the AVOS HTTP API over `fetch` (see
 * `./services.ts`), which the API server translates into real `@avos/core-api`
 * operations. The web boundary stays intact: no backend runtime is imported
 * here.
 *
 * Navigation is client-side: clicking a primary-nav link swaps the screen and
 * updates the URL via the History API without a full page reload, so the app
 * feels like a single-page application while remaining fully server-renderable.
 */
import {
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { I18nProvider } from "../i18n/index.js";
import { matchRoute, type ScreenKey } from "../shell/routes.js";
import { ArchMark } from "../console/ArchMark.js";
import HomeScreen from "../screens/HomeScreen.js";
import SignInScreen from "../screens/SignInScreen.js";
import AccountScreen from "../screens/AccountScreen.js";
import RoleManagementScreen from "../screens/RoleManagementScreen.js";
import LoginScreen from "../screens/LoginScreen.js";
import MfaChallengeScreen from "../screens/MfaChallengeScreen.js";
import MfaEnrollmentScreen from "../screens/MfaEnrollmentScreen.js";
import RegistrationScreen from "../screens/RegistrationScreen.js";
import EmailVerificationScreen from "../screens/EmailVerificationScreen.js";
import { ForgotPasswordScreen, ResetPasswordScreen, InviteAcceptScreen, ChangePasswordScreen } from "../screens/AccessAuthScreens.js";
import ImpersonationConsentScreen from "../screens/ImpersonationConsentScreen.js";
import DataExportScreen from "../screens/DataExportScreen.js";
import AccountDeletionScreen from "../screens/AccountDeletionScreen.js";
import {
  apiRegister,
  apiLogin,
  apiVerify,
  apiRequestNewVerification,
  apiForgotPassword,
  apiResetPassword,
  apiChangePassword,
  apiInviteLookup,
  apiInviteAccept,
} from "./services.js";

/** Render the screen for a route key, injecting live API callbacks. */
function renderScreen(screen: ScreenKey): ReactElement {
  switch (screen) {
    case "home":
      return <HomeScreen />;
    case "signIn":
      return <SignInScreen />;
    case "account":
      return <AccountScreen />;
    case "login":
      return <LoginScreen onLogin={apiLogin} />;
    case "mfaChallenge":
      return <MfaChallengeScreen />;
    case "mfaEnrollment":
      return <MfaEnrollmentScreen />;
    case "register":
      return <RegistrationScreen onRegister={apiRegister} />;
    case "verifyEmail":
      return (
        <EmailVerificationScreen
          onVerify={apiVerify}
          onRequestNew={apiRequestNewVerification}
        />
      );
    case "roleManagement":
      return <RoleManagementScreen />;
    case "impersonationConsent":
      return <ImpersonationConsentScreen />;
    case "dataExport":
      return <DataExportScreen />;
    case "accountDeletion":
      return <AccountDeletionScreen />;
    default:
      return <HomeScreen />;
  }
}

/**
 * Layout frame matching the server shell (banner + main landmark). The old
 * foundation horizontal navigation has been removed from the public/auth shell
 * — auth screens are a clean, focused surface; authenticated navigation lives
 * in the app shell (sidebar). Screens navigate via injected callbacks / links.
 */
function Layout({
  activePath,
  children,
}: {
  readonly activePath: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div data-app-shell="avos" data-active-route={activePath}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header role="banner">
        <span className="brand">
          <ArchMark size={22} baselineColor="#101828" />
          <span className="brand__name">AVOS</span>
        </span>
      </header>
      <main id="main-content" role="main" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}

/** The client app: route state + History API navigation + injected services. */
export function ClientApp({
  initialPath,
}: {
  readonly initialPath: string;
}): ReactElement {
  const [path, setPath] = useState(initialPath);

  // Keep the rendered screen in sync with browser back/forward. (The public
  // auth shell no longer renders a route-list nav; screens drive their own
  // transitions, e.g. login → verify, via full navigations / injected flows.)
  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Access-auth routes are handled explicitly (not in the shell route table).
  const norm = path.split(/[?#]/)[0] ?? path;
  let content: ReactElement;
  let activePath = norm;
  if (norm === "/forgot-password") {
    content = <ForgotPasswordScreen onSubmit={apiForgotPassword} />;
  } else if (norm === "/reset-password") {
    content = <ResetPasswordScreen onSubmit={apiResetPassword} />;
  } else if (norm === "/change-password") {
    content = <ChangePasswordScreen onSubmit={apiChangePassword} />;
  } else if (norm === "/invite/accept") {
    content = <InviteAcceptScreen onLookup={apiInviteLookup} onAccept={apiInviteAccept} />;
  } else {
    const route = matchRoute(path);
    activePath = route.path;
    content = renderScreen(route.screen);
  }

  return (
    <I18nProvider>
      <Layout activePath={activePath}>{content}</Layout>
    </I18nProvider>
  );
}
